import React, { useState, useEffect } from 'react';
import { CommunicationHub } from '../CommunicationHub/CommunicationHub';
import { ApiRoutes } from '../../../api/routes';

interface MessageThread {
  id: string;
  title: string;
  notificationId?: string; // Associated notification ID if this thread triggered one
  lastMessage: string;
  participants: string[];
  lastActivity: string;
  unreadCount: number;
}

export const MessagesView: React.FC = () => {
  const [activeThread, setActiveThread] = useState<string | null>(null);
  const [threads, setThreads] = useState<MessageThread[]>([
    {
      id: 'thread-001',
      title: 'SSO Configuration Discussion',
      notificationId: 'notif-003', // This thread has an associated notification
      lastMessage: 'Can you review the SSO configuration requirements?',
      participants: ['pm@tyler.com', 'tech-lead@tyler.com', 'client@example.com'],
      lastActivity: '2024-08-24T10:30:00Z',
      unreadCount: 2
    },
    {
      id: 'thread-002',
      title: 'Data Migration Planning',
      lastMessage: 'Updated the migration timeline based on your feedback',
      participants: ['pm@tyler.com', 'data-architect@tyler.com'],
      lastActivity: '2024-08-24T09:15:00Z',
      unreadCount: 0
    },
    {
      id: 'thread-003',
      title: 'Go-Live Preparation',
      lastMessage: 'Final checklist for the go-live next week',
      participants: ['pm@tyler.com', 'client@example.com', 'support@tyler.com'],
      lastActivity: '2024-08-24T08:45:00Z',
      unreadCount: 1
    }
  ]);

  const handleThreadOpen = async (threadId: string) => {
    setActiveThread(threadId);
    
    // Find the thread and check if it has an associated notification
    const thread = threads.find(t => t.id === threadId);
    if (thread?.notificationId) {
      try {
        // Acknowledge the notification to suppress Outlook email within grace window
        const response = await ApiRoutes.acknowledgeNotification(thread.notificationId);
        if (response.success) {
          console.log(`Notification ${thread.notificationId} acknowledged for thread ${threadId}`);
          
          // Update thread to show it's been acknowledged
          setThreads(prev => prev.map(t => 
            t.id === threadId 
              ? { ...t, notificationId: undefined } // Remove notification association
              : t
          ));
        } else {
          console.error('Failed to acknowledge notification:', response.error);
        }
      } catch (error) {
        console.error('Error acknowledging notification:', error);
      }
    }
  };

  const handleChannelChange = (channelId: string) => {
    console.log('Channel changed:', channelId);
    // If this is a new thread, acknowledge any associated notifications
    if (channelId !== activeThread) {
      handleThreadOpen(channelId);
    }
  };

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
            Project Communications
          </h2>
          <p className="text-gray-600 text-lg drop-shadow-sm">
            Collaborate with your project team through secure messaging
          </p>
          
          {/* Notification acknowledgment info */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center text-sm text-blue-800">
              <span className="mr-2">ℹ️</span>
              <span>
                <strong>Pro tip:</strong> Opening a message thread automatically acknowledges any associated notifications, 
                preventing duplicate Outlook emails within the 3-minute grace window.
              </span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Thread List */}
          <div className="lg:col-span-1">
            <div className="tyler-card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Threads</h3>
              <div className="space-y-2">
                {threads.map((thread) => (
                  <div
                    key={thread.id}
                    onClick={() => handleThreadOpen(thread.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      activeThread === thread.id
                        ? 'bg-[#4267A1] text-white'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className={`font-medium text-sm ${
                        activeThread === thread.id ? 'text-white' : 'text-gray-900'
                      }`}>
                        {thread.title}
                      </h4>
                      {thread.notificationId && (
                        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                          New
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mb-2 line-clamp-2 ${
                      activeThread === thread.id ? 'text-blue-100' : 'text-gray-600'
                    }`}>
                      {thread.lastMessage}
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <span className={activeThread === thread.id ? 'text-blue-100' : 'text-gray-500'}>
                        {thread.participants.length} participants
                      </span>
                      {thread.unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          {thread.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Chat Area */}
          <div className="lg:col-span-2">
            <div className="tyler-card h-[600px]">
              <CommunicationHub 
                initialChannelId={activeThread || "project-channel"}
                onChannelChange={handleChannelChange}
                onMessageSent={(message) => console.log('Message sent:', message)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
